import React, { useRef, useState, useEffect } from "react";
import {
  App,
  Card,
  Button,
  Typography,
  Modal,
  Input,
  message as antMessage,
  Grid,
} from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import CustomerApprovedFilters from "./Components/CustomerApprovedFilters";
import CustomerApprovedTable from "./Components/CustomerApprovedTable";
import ReportDom from "../../components/common/ReportDom";
import dayjs from "dayjs";
import { fetchApprovedCustomerList } from "./CustomerApprovedThunk";
import {
  customerStatusChangeApi,
  getConversationApi,
  saveConversationApi,
} from "../../apiservice/crm";
import { getAccessType } from "../../lib/functions";

const { Title } = Typography;
const { TextArea } = Input;

const CustomerApprovedRecord = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { filters, list, loading } = useSelector(
    (state) => state.customerApproved
  );

  const [entryModal, setEntryModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [conversationText, setConversationText] = useState("");
  const [conversations, setConversations] = useState([]);
  const [saving, setSaving] = useState(false);
  let [access_type, access_type_set] = useState("");

  const reportRef = useRef(null);


  useEffect(() => {
    const access = getAccessType("approved_customer_list");
    access_type_set(access);
  }, []);

  useEffect(() => {
    dispatch(fetchApprovedCustomerList({}));
  }, [dispatch]);

  const handleAdd = (customer) => {
    setSelectedCustomer(customer);
    setEntryModal(true);
  };

  const handleView = async (customer) => {
    setSelectedCustomer(customer);
    setConversations([]);
    setViewModal(true);
    // fetch conversations API
    try {
      const res = await getConversationApi({ customer_id: customer.id });
      setConversations(res.data || []);
    } catch (err) {
      antMessage.error("Failed to load conversation");
    }
  };

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: "Approved Customers Report",
    removeAfterPrint: true,
  });

  const handleAction = async (customer, status) => {
    Modal.confirm({
      title: `Are you sure you want to ${status === "p" ? "Pending" : "Reject"
        } ${customer.acc_name}?`,
      onOk: async () => {
        try {
          await customerStatusChangeApi({ customerId: customer.id, status });
          dispatch(fetchApprovedCustomerList(filters));
          antMessage.success("Action completed successfully ✅");
        } catch (err) {
          antMessage.error("Failed to perform action ❌");
        }
      },
    });
  };

  const handleSaveConversation = async () => {
    if (!conversationText.trim()) {
      antMessage.warning("Conversation text is required");
      return;
    }
    setSaving(true);
    try {
      await saveConversationApi({
        customer_id: selectedCustomer.id,
        text: conversationText,
      });
      setConversationText("");
      setEntryModal(false);
      antMessage.success("Conversation saved ✅");
    } catch (err) {
      antMessage.error("Failed to save conversation ❌");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4">
      <App>
        <Card
          className="shadow-md rounded-xl mb-4"
          title={
            <Title level={4} style={{ margin: 0 }}>
              Approved Customer List
            </Title>
          }
          extra={
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              { !screens.xs && "Print" }
            </Button>
          }
          styles={{
            header: { borderBottom: "none", padding: "12px 16px" },
            body: { padding: 0 },
          }}
        />

        <CustomerApprovedFilters messageApi={message} />

        <Card className="shadow-md rounded-xl" >
          <CustomerApprovedTable
            list={list}
            loading={loading}
            onAdd={handleAdd}
            onView={handleView}
            onPending={(c) => handleAction(c, "p")}
            onReject={(c) => handleAction(c, "r")}
          />
        </Card>
      </App>

      {/* Hidden Print */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Approved Customer List"
          mode="report-page"
          filters={filters}
          renderTable={() => <CustomerApprovedTable list={list} printMode />}
        />
      </div>

      {/* Add Conversation Modal */}
      <Modal
        open={entryModal}
        title={`Add Conversation - ${selectedCustomer?.acc_name}`}
        onCancel={() => setEntryModal(false)}
        footer={null}
      >
        <TextArea
          rows={6}
          value={conversationText}
          onChange={(e) => setConversationText(e.target.value)}
          placeholder="Enter conversation..."
        />
        {access_type == "WRITE" && <>
          <Button
            type="primary"
            loading={saving}
            onClick={handleSaveConversation}
            block
            style={{ marginTop: "10px" }}
          >
            Save
          </Button>
        </>}

      </Modal>

      {/* View Conversation Modal */}
      <Modal
        open={viewModal}
        title={`Conversation History - ${selectedCustomer?.acc_name}`}
        onCancel={() => setViewModal(false)}
        footer={null}
      >
        {conversations.length ? (
          conversations.map((c, i) => (
            <div
              key={i}
              style={{ marginBottom: "10px", whiteSpace: "pre-line" }}
            >
              <b>{i + 1}.</b> {c.text || c.conversation} <br />
              <small>{dayjs(c.created_date).format("DD MMM YYYY")}</small>
            </div>
          ))
        ) : (
          <p>No conversation found</p>
        )}
      </Modal>
    </div>
  );
};

export default CustomerApprovedRecord;
